// src/pages/MyEventsPage.js
import React, { useEffect, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { Link } from 'react-router-dom';
import EventCard from '../components/EventCard';
import ReactPaginate from 'react-paginate';
import axios from 'axios';
import config from '../config/config';
import LoadingModal from '../components/modals/LoadingModal';
import { useNavigate } from 'react-router-dom';

const MyEventsPage = () => {
  const { currentUser, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const [myEvents, setMyEvents] = useState([]);
  const [currentPage, setCurrentPage] = useState(0);
  const [eventsPerPage] = useState(6); // Adjust as needed
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchMyEvents = async () => {
      if (!currentUser || !currentUser.token) {
        console.error("No user or token available");
        setLoading(false);
        return;
      }
      
      setLoading(true);
      
      try {
        const response = await axios.get(
          `${config.BASE_URL}/api/events/my-events`, 
          {
            headers: { Authorization: `Bearer ${currentUser.token}` }
          }
        );
        
        
        if (response.data && response.data.success) {
          setMyEvents(response.data.events || []);
        } else {
          console.error("API returned success: false");
          setError('Failed to fetch your events.');
        }
      } catch (err) {
        console.error('Error fetching my events:', err);
        setError(`Failed to fetch your events: ${err.message}`);
      } finally {
        setLoading(false);
      }
    };

    if (isAuthenticated && currentUser) {
      fetchMyEvents();
    } else {
      // If not authenticated, redirect to login
      setLoading(false);
      navigate("/login");
    }
  }, [currentUser, isAuthenticated, navigate]);

  const handlePageClick = (data) => {
    setCurrentPage(data.selected);
  };

  const handleDelete = async (eventId) => {
    if (!window.confirm("Are you sure you want to delete this event?")) {
      return;
    }

    try {
      setLoading(true);
      const response = await axios.delete(
        `${config.BASE_URL}/api/events/${eventId}/delete`, 
        {
          headers: { Authorization: `Bearer ${currentUser.token}` }
        }
      );
      
      if (response.data.success) {
        // Remove the deleted event from the state
        setMyEvents(myEvents.filter((event) => event.id !== eventId));
        alert('Event deleted successfully.');
      } else {
        alert('Failed to delete the event.');
      }
    } catch (err) {
      console.error('Error deleting event:', err);
      alert(`Failed to delete the event: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const offset = currentPage * eventsPerPage;
  const currentEvents = myEvents.slice(offset, offset + eventsPerPage);
  const pageCount = Math.ceil(myEvents.length / eventsPerPage);

  if (loading) {
    return <LoadingModal message="Loading your events..." />;
  }

  if (!isAuthenticated) {
    return (
      <div className="container mt-5 pt-5">
        <div className="alert alert-warning">
          <h4>Authentication Required</h4>
          <p>Please log in to view your events.</p>
          <button className="button" onClick={() => navigate('/login')}>
            Go to Login
          </button>
        </div>
      </div>
    );
  }

  return (
    <section id="my-events" className="pt-4 pb-5">
      <br/><br/><br/><br/>
      <div className="container">
        <div className="row mb-4">
          <div className="col-md-6">
            <h2 className="text-white">My Events</h2>
          </div>
          <div className="col-md-6 text-end">
            <Link to="/create-event" className="button">
              <i className="fa fa-plus me-2"></i>Create New Event
            </Link>
          </div>
        </div>

        {error && (
          <div className="alert alert-danger">{error}</div>
        )}

        {myEvents.length === 0 && !error ? (
          <div className="alert alert-info">
            You haven't created any events yet. <Link to="/create-event">Create your first event</Link>.
          </div>
        ) : (
          <>
            <div className="row">
              {currentEvents.map((event) => (
                <EventCard key={event.id} event={event} onDelete={handleDelete} />
              ))}
            </div>
            
            {/* Pagination Controls */}
            {pageCount > 1 && (
              <ReactPaginate
                previousLabel={'previous'}
                nextLabel={'next'}
                breakLabel={'...'}
                breakClassName={'break-me'}
                pageCount={pageCount}
                marginPagesDisplayed={2}
                pageRangeDisplayed={5}
                onPageChange={handlePageClick}
                containerClassName={'pagination justify-content-center mt-4'}
                pageClassName={'page-item'}
                pageLinkClassName={'page-link'}
                previousClassName={'page-item'}
                nextClassName={'page-item'}
                previousLinkClassName={'page-link'}
                nextLinkClassName={'page-link'}
                activeClassName={'active'}
              />
            )}
          </>
        )}
      </div>
    </section>
  );
};

export default MyEventsPage;
